/**
 * Human-readable date helpers for the UI. The API returns dates as
 * `YYYY-MM-DD` strings in the user's timezone, and the raw form is jarring
 * for end users (e.g. `2026-05-04 — 2026-05-10`). These helpers turn that
 * into the locale's natural form (`4–10 мая 2026` / `May 4–10, 2026`).
 */

type Lang = 'ru' | 'en'

/** Parse a `YYYY-MM-DD` string into a Date in the user's local zone. */
function parseYmd(ymd: string): Date {
  return new Date(ymd + 'T00:00:00')
}

/**
 * Format a single birth date in the locale's "long" form.
 * `formatBirthDate('1985-05-09', 'ru')` → `9 мая 1985 г.` → trimmed.
 */
export function formatBirthDate(ymd: string, lang: Lang): string {
  const d = parseYmd(ymd)
  return d
    .toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(/\s*г\.?$/, '') // RU locale appends " г." — strip it
}

/**
 * Format a week range. Compresses same-month / same-year into the natural
 * shape so it reads tightly:
 *   • Same month: `4–10 мая 2026` / `May 4–10, 2026`
 *   • Different months, same year: `28 апреля — 4 мая 2026`
 *   • Different years: `30 декабря 2025 — 5 января 2026`
 *
 * Russian uses GENITIVE month names after a number (`10 мая`, not `10 май`).
 * Intl.DateTimeFormat returns the genitive form only when both `day` and
 * `month` are requested together, so we always format from a real Date with
 * day+month rather than reading just the month name.
 */
export function formatWeekRange(start: string, end: string, lang: Lang): string {
  const s = parseYmd(start)
  const e = parseYmd(end)
  const locale = lang === 'en' ? 'en-US' : 'ru-RU'
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
  const sameYear = s.getFullYear() === e.getFullYear()

  if (sameMonth) {
    if (lang === 'en') {
      const month = s.toLocaleDateString('en-US', { month: 'long' })
      return `${month} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`
    }
    // Format the END as "10 мая 2026" (genitive) and prepend start day:
    // "4–10 мая 2026". Intl gives genitive when day is part of the format.
    const endStr = e
      .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
      .replace(/\s*г\.?$/, '')
    return `${s.getDate()}–${endStr}`
  }

  const dayMonth = (d: Date) =>
    d.toLocaleDateString(locale, { day: 'numeric', month: 'long' })
  const fullDate = (d: Date) =>
    d
      .toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
      .replace(/\s*г\.?$/, '')

  if (sameYear) {
    return `${dayMonth(s)} — ${fullDate(e)}`
  }
  return `${fullDate(s)} — ${fullDate(e)}`
}
